import React from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, ScrollText } from "lucide-react";
import { Section, Eyebrow } from "../components/site/Section";
import { useAuth, buildEmergentLoginUrl } from "../lib/auth";

export default function LoginPage() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/archive/drop" replace />;

  const onSignIn = () => {
    window.location.href = buildEmergentLoginUrl("/auth/callback");
  };

  return (
    <div data-testid="login-page">
      <Section testId="login-hero" className="pt-12 md:pt-24">
        <div className="ornate-rule text-[10px] tracking-[0.42em] uppercase text-[#C5A059]">
          <span>The Keeper's Threshold</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-3xl">
          Sign in to <span className="text-[#C5A059] italic font-light">expand the cast</span>.
        </h1>
        <p className="mt-6 text-base md:text-lg text-[#A39E99] max-w-2xl leading-relaxed">
          The Archive remembers every record you recover. Your guesses, your discoveries,
          your standing on the leaderboard — sealed under your name forever.
        </p>

        <div className="mt-10 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 bg-[#0A0A0A] border border-[#C5A059]/30 p-7 md:p-9" style={{ boxShadow: "0 0 80px -30px #C5A059" }}>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059]">Choose your sigil</div>
            <p className="text-sm text-[#A39E99] mt-3 leading-relaxed">
              One click. No password. The Archive will remember.
            </p>
            <button
              onClick={onSignIn}
              data-testid="login-google-btn"
              className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#EAE0D5] text-black px-7 py-4 text-xs tracking-[0.26em] uppercase hover:bg-white transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c2.9 0 5.6 1 7.7 2.7l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-23.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c2.9 0 5.6 1 7.7 2.7l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.5-5.2l-6.2-5.2A12 12 0 0 1 12.7 28.6L6.1 33.7A20 20 0 0 0 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2A19.9 19.9 0 0 0 44 24c0-1.2-.1-2.4-.4-3.5z"/>
              </svg>
              Sign in with Google
            </button>
            <p className="mt-4 text-[10px] tracking-[0.24em] uppercase text-[#737373]">
              We only see your name and email. Nothing else.
            </p>
          </div>
          <div className="md:col-span-5 bg-[#0A0A0A] border border-white/5 p-7 md:p-9">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">What an account unlocks</div>
            <ul className="mt-5 space-y-3 text-sm text-[#A39E99]">
              {[
                "Your personal cast — only the records you cracked, with full lore",
                "Your archive — overall progress, accuracy, nation-by-nation breakdown",
                "Leaderboard standing during con weekend",
                "Eligibility for the $500 Anime NYC trivia prize",
                "Code redemptions for sealed-record handouts",
              ].map((it) => (
                <li key={it} className="flex gap-3">
                  <span className="text-[#C5A059] mt-0.5">·</span>
                  <span className="leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10">
          <Link
            to="/archive"
            data-testid="login-browse-anon"
            className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#C5A059] hover:underline"
          >
            <ScrollText size={14} /> Or browse the public archive wall first <ArrowRight size={14} />
          </Link>
        </div>
      </Section>
    </div>
  );
}
